import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { editLock } from "actions/Units";
import TextInput from "components/TextInput";
import SelectInput from "components/SelectInput";
import ButtonContained from "components/ButtonContained";
import LoadingIndicator from "components/LoadingIndicator";

const LockSettingsInformationModal = ({ lock, unitUUID }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  
  const fetchedLock = useSelector(state => state.units.unitObject?.unit?.locks.find(l => l.id === lock.id));
  const authUser = useSelector(state => state.auth.authUser);
  const site = useSelector(state => state.auth.site);
  const [localLock, setLocalLock] = useState(null);
  const originalLock = useRef(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (fetchedLock) {
      setLocalLock(fetchedLock);
      originalLock.current = fetchedLock;
    }
  }, [fetchedLock]);

  useEffect(() => {
    if (localLock && originalLock.current) {
      const isChanged = JSON.stringify(localLock) !== JSON.stringify(originalLock.current);
      setHasChanges(isChanged);
    }
  }, [localLock]);

  const handleInputChange = (field, value) => {
    setLocalLock((prevLock) => ({
      ...prevLock,
      [field]: value,
    }));
  };

  return (
    localLock ? <div className="min-h-[300px] px-10 pt-4 flex flex-col gap-8">
      <div>
        <TextInput
          id="Lock-Settings-Device-Name-Input"
          dataCy="Lock-Settings-Device-Name-Input"
          placeholder={intl.formatMessage({id: "form.name"})}
          onChange={(e) => handleInputChange("name", e.target.value)}
          value={localLock?.name ?? ""}
        />
      </div>
      <div>
        <SelectInput
          id="Lock-Settings-Hardware-Type-Input"
          dataCy="Lock-Settings-Hardware-Type-Input"
          value={localLock?.hwType ?? ""}
          placeholder={intl.formatMessage({ id: "form.hwType" })}
          onChange={(e) => handleInputChange("hwType", e.target.value)}
          options={[
            { name: intl.formatMessage({ id: 'db.unit' }), value: 'unit' },
            { name: intl.formatMessage({ id: 'db.gate' }), value: 'gate' },
            { name: intl.formatMessage({ id: 'db.exitgate' }), value: 'exitgate' },
            { name: intl.formatMessage({ id: 'db.door' }), value: 'door' },
            { name: intl.formatMessage({ id: 'db.sample' }), value: 'sample' },
            { name: intl.formatMessage({ id: 'db.repeater' }), value: 'repeater' },
            { name: intl.formatMessage({ id: 'db.elevator' }), value: 'elevator' },
            { name: intl.formatMessage({ id: 'db.slidingdoor' }), value: 'slidingdoor' },
            { name: intl.formatMessage({ id: 'db.alarm' }), value: 'alarm' },
            { name: intl.formatMessage({ id: 'db.padlock' }), value: 'padlock' },
            { name: intl.formatMessage({ id: 'db.demo' }), value: 'demo' }
          ]}
        />
      </div>
      <div>
        <SelectInput
          id="Lock-Settings-Unit-Alarm-Input"
          dataCy="Lock-Settings-Unit-Alarm-Input"
          value={localLock?.alarmsOn ?? false}
          placeholder={intl.formatMessage({ id: "form.unitAlarm" })}
          onChange={(e) => handleInputChange("alarmsOn", e.target.value === "true")}
          options={[
            {
              name: intl.formatMessage({ id: "form.activate" }),
              value: true
            },
            {
              name: intl.formatMessage({ id: "form.deactivate" }),
              value: false
            },
          ]}
        />
      </div>
      {["1A", "2A", "2E", "3E", "4E", "5E"].includes(localLock?.bleHwVersion) && <div>
        <SelectInput
          id="Lock-Settings-Front-Motion-Input"
          dataCy="Lock-Settings-Front-Motion-Input"
          value={localLock?.frontMotion ?? false}
          placeholder={intl.formatMessage({ id: "comp.table.header.frontMotion" })}
          onChange={(e) => handleInputChange("frontMotion", e.target.value === "true")}
          options={[
            {
              name: intl.formatMessage({ id: "form.activate" }),
              value: true
            },
            {
              name: intl.formatMessage({ id: "form.deactivate" }),
              value: false
            },
          ]}
        />
      </div>}
      <div>
        <ButtonContained
          id="Lock-Settings-Information-Save-Changes-Button"
          dataCy="Lock-Settings-Information-Save-Changes-Button"
          text={intl.formatMessage({ id: "comp.button.saveChanges" })}
          type="default"
          disabled={!hasChanges}
          onClick={() => dispatch(
            editLock(
              {
                ...localLock,
                unitUUID: unitUUID,
                lockId: localLock.id,
                lockUUID: localLock.uuid
              },
              {
                success: intl.formatMessage({ id: "notifications.success.edit" }),
                refetch: true,
              }
            )
          )}
        />
      </div>
    </div> : <div className="min-h-[300px] flex justify-center">
      <LoadingIndicator />
    </div>
  )
};

export default LockSettingsInformationModal;